/**
 * MXlab — Récapitulatif.
 *
 * Deux sources, deux mécanismes :
 *
 * 1. LE KIT — rendu par Liquid. Au changement de matière, on extrait le fragment
 *    `[ref="summaryBody"]` du HTML re-rendu par la Section Rendering API et on le
 *    remplace. Même pattern que `product-price` d'Horizon. Aucun calcul de prix
 *    n'est refait en JS : il vient des variantes réelles.
 *
 * 2. LES ACCESSOIRES — hors du formulaire de variantes. `mx-options` émet un
 *    événement `mx:options`, on l'écoute et on ajoute les lignes.
 *
 * Le total général est la seule chose calculée ici : prix de la variante + accessoires.
 */
/**
 * Reproduit le filtre `money` de Liquid a partir de shop.money_format.
 * Volontairement duplique plutot qu'importe : `@theme/*` est un import map
 * explicite dans snippets/scripts.liquid. Y ajouter une entree obligerait a
 * modifier un fichier du theme — et un import non resolu tue tout le module.
 *
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const value = cents / 100;

  const group = (num, thousands, decimal, decimals) => {
    const fixed = num.toFixed(decimals);
    const [int, frac] = fixed.split('.');
    const spaced = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? `${spaced}${decimal}${frac}` : spaced;
  };

  return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    switch (token) {
      case 'amount':
        return group(value, ',', '.', 2);
      case 'amount_no_decimals':
        return group(value, ',', '.', 0);
      case 'amount_with_comma_separator':
        return group(value, '.', ',', 2);
      case 'amount_no_decimals_with_comma_separator':
        return group(value, '.', ',', 0);
      case 'amount_with_space_separator':
        return group(value, ' ', ',', 2);
      case 'amount_no_decimals_with_space_separator':
        return group(value, ' ', ',', 0);
      case 'amount_with_apostrophe_separator':
        return group(value, "'", '.', 2);
      default:
        return group(value, ',', '.', 2);
    }
  });
}

/**
 * CHANGEMENT DE VARIANTE — `shopify:product:select` (@shopify/events).
 *
 * L'EVENEMENT EST EMIS AVANT LE FETCH, et c'est ce qui dicte tout le reste : au moment
 * ou il arrive, le nouveau rendu n'existe pas encore. Les donnees sont livrees par
 * `await event.promise` -> { variant, detail: { html, productId, newProduct, sourceId,
 * resource } }. `normalizeVariantEvent` attend cette promesse et ramene le resultat a
 * { resource, data, target } ; le reste du composant est synchrone et ne voit rien.
 */
const MODERN_VARIANT_EVENT = 'shopify:product:select';

/**
 * Diffusé par `MxGabarit` (assets/mx-compat.js) à chaque choix de découpe, ou sa levée.
 * Littéral dupliqué à dessein (même arbitrage que le reste du thème pour les noms
 * d'événement inter-modules) : aucune entrée ajoutée à l'import map native.
 */
const DECOUPE_EVENT = 'mx:decoupe-changed';

/**
 * Attend la promesse de l'evenement, puis passe { resource, data, target } a `apply`.
 *
 * @param {Event & { promise?: Promise<any> }} event
 * @param {(payload: { resource: any, data: any, target: EventTarget | null }) => void} apply
 */
function normalizeVariantEvent(event, apply) {
  const target = event.target;

  event.promise
    .then((result) => {
      const detail = result?.detail;
      if (!detail) return;
      // `resource` = le JSON de variante, comme event.detail.resource en 2.1.5.
      // Repli sur result.variant, que le natif resout aussi (variant-picker.js:392).
      apply({ resource: detail.resource ?? result?.variant ?? null, data: detail, target });
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[mx-summary] promesse de variante rejetee :', error);
    });
}

class MxSummary extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.moneyFormat = this.dataset.moneyFormat;
    this.accList = this.querySelector('[data-mx-acc]');
    this.grandEl = this.querySelector('[data-mx-grand]');

    this.accessories = [];
    this.lastDecoupe = null;

    document.addEventListener(MODERN_VARIANT_EVENT, this.onVariant);
    document.addEventListener('mx:options', this.onOptions);
    document.addEventListener(DECOUPE_EVENT, this.onDecoupe);

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(MODERN_VARIANT_EVENT, this.onVariant);
    document.removeEventListener('mx:options', this.onOptions);
    document.removeEventListener(DECOUPE_EVENT, this.onDecoupe);
  }

  get body() {
    return this.querySelector('[ref="summaryBody"]');
  }

  get kitPrice() {
    return parseInt(this.body?.dataset.kitPrice, 10) || 0;
  }

  /**
   * Point d'entree de l'evenement de variante.
   *
   * GARDE DE SIGNATURE, a ne pas confondre avec l'ancien verrou de famille (retire le
   * 05/08/2026 avec la double ecoute) : elle protege d'un `shopify:product:select` emis
   * par une app tierce SANS promesse, sur lequel `.then()` leverait un TypeError.
   */
  onVariant = (event) => {
    if (typeof event?.promise?.then !== 'function') return;
    normalizeVariantEvent(event, this.applyVariant);
  };

  /** Le kit a changé : on remplace le fragment rendu par le serveur. */
  applyVariant = ({ data, target }) => {
    if (!data?.html) return;

    // Une autre fiche produit sur la page ne doit pas nous impacter.
    if (
      !data.newProduct &&
      target instanceof HTMLElement &&
      target.dataset.productId &&
      target.dataset.productId !== this.productId
    ) {
      return;
    }

    const fresh = data.html.querySelector('mx-summary [ref="summaryBody"]');
    const current = this.body;
    if (!fresh || !current) return;

    if (current.innerHTML !== fresh.innerHTML || current.dataset.kitPrice !== fresh.dataset.kitPrice) {
      current.replaceWith(fresh);
    }

    // Le fragment vient d'être remplacé par du HTML fraîchement rendu par Liquid : il ne
    // porte pas la découpe (donnée client, jamais connue du serveur). On la réapplique
    // depuis le dernier événement reçu, sinon un changement de variante ferait disparaître
    // la découpe déjà affichée dans le récap.
    this.applyDecoupe();
    this.render();
  };

  /** Point d'entrée de l'événement de découpe (point 6). */
  onDecoupe = (event) => {
    const { productId, decoupe } = event.detail || {};
    if (productId && this.productId && String(productId) !== String(this.productId)) return;
    this.lastDecoupe = decoupe || '';
    this.applyDecoupe();
  };

  /**
   * Démasque la ligne de cylindrée déjà rendue par Liquid (montant compris) et y pose le
   * nom de la découpe choisie — jamais de recalcul de prix ici, uniquement du texte.
   *
   * `decoupe` is already the target text: the gabarit name alone (`data-mx-gab-nom`,
   * snippets/mx-decoupe.liquid), without the model, which the page title already shows.
   * No concatenation with the cylindrée band here: the gabarit name already repeats it.
   *
   * Sans découpe choisie — ou sur un produit sans cylindrée, qui ne porte pas ce marqueur
   * — la ligne reste masquée : rien plutôt qu'une ligne vide.
   */
  applyDecoupe() {
    const decoupe = this.lastDecoupe || '';
    const line = this.querySelector('[data-mx-cyl-line]');
    const label = this.querySelector('[data-mx-cyl-label]');
    if (!line || !label) return;

    if (decoupe) {
      label.textContent = decoupe;
      line.hidden = false;
    } else {
      line.hidden = true;
    }
  }

  /** Les accessoires ont changé. */
  onOptions = (event) => {
    const { productId, items } = event.detail || {};
    if (productId && String(productId) !== String(this.productId)) return;
    this.accessories = items || [];
    this.render();
  };

  render() {
    // Lignes d'accessoires
    if (this.accList) {
      this.accList.replaceChildren();
      this.accList.hidden = this.accessories.length === 0;

      for (const item of this.accessories) {
        const li = document.createElement('li');
        li.className = 'mx-sum__line';

        const label = document.createElement('span');
        label.className = 'mx-sum__label';
        label.textContent = item.title;

        const value = document.createElement('span');
        value.className = 'mx-sum__value';
        value.textContent = `+${formatMoney(item.price, this.moneyFormat)}`;

        li.append(label, value);
        this.accList.appendChild(li);
      }
    }

    // Total général
    const total = this.accessories.reduce((sum, item) => sum + item.price, this.kitPrice);
    if (this.grandEl) this.grandEl.textContent = formatMoney(total, this.moneyFormat);
  }
}

if (!customElements.get('mx-summary')) {
  customElements.define('mx-summary', MxSummary);
}